// @ts-check
// Barra de view (doc 06, seções 2 e 3): seletor de views, Filtro, Classificar e a linha de
// chips com "Redefinir" e "Salvar" quando há mudanças não salvas. Na lista aberta por um clique num gráfico, o chip
// "Do painel" vem antes dos filtros e o "Salvar" some, para o recorte não ir para a view de equipe (contrato 07, seção 10).
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Dropdown, DropdownMenuItems, DropdownMenuSeparator, MenuItem } from '../../ui/dropdown.js';
import { Icon } from '../../ui/icon.js';
import { Button, LightButton } from '../../ui/button.js';
import { locationState } from '../../app/router.js';
import { fieldsById, hasCurrentPermissionFlag } from '../../data/metadata-store.js';
import {
  getFieldIcon,
  getSortSubFields,
  isFieldDisplayable,
  isFieldFilterable,
  isFieldSortable,
} from '../../lib/field-metadata.js';
import { getDefaultOperand, getFilterDisplayValue, getFilterSubFields, getSubFieldLabel, operandNeedsValue } from '../../lib/view-filters.js';
import { FieldSelectMenu, FilterEditor } from './filter-editor.js';
import { ExportRecordsButton } from './export-records-button.js';
import { ImportRecordsButton } from '../spreadsheet-import/import-records-button.js';
import { canUpdateObjectRecords } from '../../lib/permissions.js';
import { SaveAsNewViewButton, ViewPicker } from './view-picker.js';
import { AdvancedFilterChip } from './advanced-filter.js';
import {
  addDraftFilter,
  ensureAdvancedFilterRoot,
  getViewDraft,
  isDeletedFilterActive,
  isViewDraftDirty,
  removeDraftFilter,
  removeDraftSort,
  resetViewDraft,
  saveViewDraft,
  setDeletedFilterActive,
  setDraftAnyFieldSearch,
  setDraftSort,
  syncViewUrlWithDraft,
  getViewQuery,
  updateDraftFilter,
  clearViewDrilldown,
  getViewDrilldown,
} from './view-state.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';
import { ViewOptionsButton } from './view-options.js';
import { useListCommandRequest } from '../command-menu/command-state.js';
import { DrilldownChip } from './drilldown-chip.js';
import { drilldownModeOf, recipeOf } from '../../lib/drilldown-links.js';
import { isFlowDrilldownRecipe } from '../../../../shared/dashboards/drilldown-recipe.js';

/**
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('../../data/metadata-store.js').View} View
 */

/**
 * @param {{ view: View, filter: import('../../lib/view-filters.js').ViewFilter }} props
 */
function FilterChip({ view, filter }) {
  const anchorRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const draft = getViewDraft(view);
  const isOpen = draft.openFilterId.value === filter.id;
  const field = fieldsById.value.get(filter.fieldMetadataId);
  if (!field) return null;
  const displayValue = getFilterDisplayValue(filter, field);
  const close = () => {
    draft.openFilterId.value = null;
    const current = draft.filters.value.find((candidate) => candidate.id === filter.id);
    // Fechar o editor com o valor vazio remove o filtro, como no Twenty.
    if (current && operandNeedsValue(current.operand) && !current.value && field.type !== 'BOOLEAN') removeDraftFilter(view, filter.id);
  };
  return html`<div class="sort-or-filter-chip" ref=${anchorRef}>
    <button type="button" class="sort-or-filter-chip-main" onClick=${() => (draft.openFilterId.value = isOpen ? null : filter.id)}>
      <${Icon} name=${getFieldIcon(field)} size=${16} />
      <span class="sort-or-filter-chip-label">${translateMetadataLabel(field.label)}${filter.subFieldName ? ` / ${getSubFieldLabel(filter.subFieldName)}` : ''}</span>
      <span class="sort-or-filter-chip-value">${t(`filters.operandsShort.${filter.operand}`)}${displayValue}</span>
    </button>
    <button type="button" class="sort-or-filter-chip-remove" aria-label=${t('viewBar.removeFilter')} onClick=${() => removeDraftFilter(view, filter.id)}>
      <${Icon} name="IconX" size=${14} />
    </button>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${close} width=${field.type === 'RELATION' || field.type === 'DATE' || field.type === 'DATE_TIME' ? 300 : 240}>
      <${FilterEditor}
        filter=${filter}
        field=${field}
        onChange=${(changes) => updateDraftFilter(view, filter.id, changes)}
        onClose=${close}
      />
    <//>
  </div>`;
}

/**
 * Chip "Excluídos" da lixeira (doc 06, SoftDeleteFilterChip): variante de perigo, sai pelo X.
 * @param {{ view: View }} props
 */
function DeletedFilterChip({ view }) {
  return html`<div class="sort-or-filter-chip" data-variant="danger" data-testid="deleted-filter-chip">
    <span class="sort-or-filter-chip-main">
      <${Icon} name="IconTrash" size=${16} />
      <span class="sort-or-filter-chip-label">${t('trash.chip')}</span>
    </span>
    <button type="button" class="sort-or-filter-chip-remove" aria-label=${t('trash.removeChip')} onClick=${() => setDeletedFilterActive(view, false)}>
      <${Icon} name="IconX" size=${14} />
    </button>
  </div>`;
}

/**
 * @param {{ objectMetadata: ObjectMetadata, view: View, label?: string, icon?: string, buttonClassName?: string, answersCommands?: boolean }} props
 *   answersCommands: o botão da barra abre também pelo menu de comandos e pelo botão fixado (só um dos dois botões)
 */
function AddFilterButton({ objectMetadata, view, label, icon, buttonClassName = 'header-dropdown-button', answersCommands = false }) {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  useListCommandRequest('filter', answersCommands ? view.id : '', () => setIsOpen(true));
  const filterableFields = objectMetadata.fields.filter((field) => isFieldFilterable(field));
  const visibleFieldIds = new Set(view.viewFields.filter((viewField) => viewField.isVisible).map((viewField) => viewField.fieldMetadataId));
  return html`<span class="view-bar-dropdown">
    <button ref=${anchorRef} type="button" class=${buttonClassName} data-open=${isOpen ? 'true' : undefined} onClick=${() => setIsOpen(!isOpen)}>
      ${icon ? html`<${Icon} name=${icon} size=${16} />` : null}
      ${label ? html`<span>${label}</span>` : null}
    </button>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} width=${320} placement="bottom-start">
      <${FieldSelectMenu}
        title=${t('viewBar.filter')}
        fields=${filterableFields}
        visibleFieldIds=${visibleFieldIds}
        getSubFields=${getFilterSubFields}
        onClose=${() => setIsOpen(false)}
        onSelect=${(field, subFieldName) => {
          setIsOpen(false);
          addDraftFilter(view, field.id, getDefaultOperand(field, subFieldName ?? null), subFieldName ?? null);
        }}
      />
      <${DropdownMenuSeparator} />
      <${DropdownMenuItems}>
        <${MenuItem} icon="IconSearch" text=${t('viewsFilters.anyField.menuItem')} className="any-field-menu-item"
          contextualText=${getViewDraft(view).anyFieldSearch.value || undefined}
          onClick=${() => {
            setIsOpen(false);
            getViewDraft(view).isAnyFieldSearchOpen.value = true;
          }} />
      <//>
      ${filterableFields.length > 0
        ? html`<${DropdownMenuSeparator} />
          <${DropdownMenuItems}>
            <${MenuItem} icon="IconFilter" text=${t('parity.advancedFilter.open')} onClick=${() => {
              setIsOpen(false);
              ensureAdvancedFilterRoot(view, filterableFields[0], getDefaultOperand(filterableFields[0]));
              requestAnimationFrame(() => (getViewDraft(view).isAdvancedFilterOpen.value = true));
            }} />
          <//>`
        : null}
      ${!isDeletedFilterActive(view)
        ? html`<${DropdownMenuSeparator} />
          <${DropdownMenuItems}>
            <${MenuItem} icon="IconTrash" text=${t('trash.filterMenuItem')} onClick=${() => {
              setIsOpen(false);
              setDeletedFilterActive(view, true);
            }} />
          <//>`
        : null}
    <//>
  </span>`;
}

/**
 * @param {{ objectMetadata: ObjectMetadata, view: View }} props
 */
function SortButton({ objectMetadata, view }) {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  useListCommandRequest('sort', view.id, () => setIsOpen(true));
  const [direction, setDirection] = useState(/** @type {'asc' | 'desc'} */ ('asc'));
  const sortableFields = objectMetadata.fields
    .filter((field) => isFieldSortable(field) && isFieldDisplayable(field))
    .sort((first, second) => translateMetadataLabel(first.label).localeCompare(translateMetadataLabel(second.label)));
  const visibleFieldIds = new Set(view.viewFields.filter((viewField) => viewField.isVisible).map((viewField) => viewField.fieldMetadataId));
  return html`<span class="view-bar-dropdown">
    <button ref=${anchorRef} type="button" class="header-dropdown-button" data-open=${isOpen ? 'true' : undefined} onClick=${() => setIsOpen(!isOpen)}>
      ${t('viewBar.sort')}
    </button>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} width=${320} placement="bottom-end">
      <${FieldSelectMenu}
        title=${t('viewBar.sort')}
        fields=${sortableFields}
        visibleFieldIds=${visibleFieldIds}
        onClose=${() => setIsOpen(false)}
        header=${html`<div class="sort-direction-row">
          ${['asc', 'desc'].map(
            (candidate) => html`<button
              type="button"
              class="segmented-item"
              data-active=${direction === candidate ? 'true' : undefined}
              onClick=${() => setDirection(/** @type {'asc' | 'desc'} */ (candidate))}
            >
              <${Icon} name=${candidate === 'asc' ? 'IconSortAscending' : 'IconSortDescending'} size=${14} />
              ${t(`viewBar.${candidate === 'asc' ? 'ascending' : 'descending'}`)}
            </button>`,
          )}
        </div>`}
        getSubFields=${(/** @type {import('../../lib/field-metadata.js').FieldMetadata} */ field) => {
          const subFields = getSortSubFields(field);
          return subFields.length > 1 ? subFields : [];
        }}
        allowAnySubField=${false}
        onSelect=${(field, subFieldName) => {
          setIsOpen(false);
          setDraftSort(view, field.id, direction, subFieldName ?? null);
        }}
      />
    <//>
  </span>`;
}

/**
 * Chip de ordenação (EditableSortChip do Twenty): clicar alterna a direção; em campo composto abre a escolha da
 * direção e do sub-campo (nome ou sobrenome, cidade ou estado, valor ou moeda).
 * @param {{ view: View, sort: import('./view-state.js').ViewSort }} props
 */
function SortChip({ view, sort }) {
  const anchorRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const field = fieldsById.value.get(sort.fieldMetadataId);
  if (!field) return null;
  const isAscending = String(sort.direction).toLowerCase() === 'asc';
  const subFields = getSortSubFields(field);
  const hasSubFieldChoice = subFields.length > 1;
  const currentSubField = sort.subFieldName && subFields.includes(sort.subFieldName) ? sort.subFieldName : subFields[0];
  return html`<div class="sort-or-filter-chip" ref=${anchorRef} data-testid="sort-chip">
    <button type="button" class="sort-or-filter-chip-main" title=${hasSubFieldChoice ? undefined : t('viewBar.toggleDirection')}
      onClick=${() => (hasSubFieldChoice ? setIsOpen(!isOpen) : setDraftSort(view, field.id))}>
      <${Icon} name=${isAscending ? 'IconArrowUp' : 'IconArrowDown'} size=${16} />
      <span class="sort-or-filter-chip-label">${translateMetadataLabel(field.label)}${hasSubFieldChoice && currentSubField ? ` / ${getSubFieldLabel(currentSubField)}` : ''}</span>
    </button>
    <button type="button" class="sort-or-filter-chip-remove" aria-label=${t('viewBar.removeSort')} onClick=${() => removeDraftSort(view, sort.id)}>
      <${Icon} name="IconX" size=${14} />
    </button>
    ${hasSubFieldChoice
      ? html`<${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} width=${220}>
          <${DropdownMenuItems}>
            ${['asc', 'desc'].map((direction) => html`<${MenuItem} key=${direction}
              icon=${direction === 'asc' ? 'IconSortAscending' : 'IconSortDescending'}
              text=${t(`viewBar.${direction === 'asc' ? 'ascending' : 'descending'}`)}
              selected=${(direction === 'asc') === isAscending}
              onClick=${() => setDraftSort(view, field.id, /** @type {'asc' | 'desc'} */ (direction))} />`)}
          <//>
          <${DropdownMenuSeparator} />
          <${DropdownMenuItems}>
            ${subFields.map((subFieldName) => html`<${MenuItem} key=${subFieldName}
              text=${getSubFieldLabel(subFieldName)}
              selected=${subFieldName === currentSubField}
              onClick=${() => {
                setDraftSort(view, field.id, isAscending ? 'asc' : 'desc', subFieldName);
                setIsOpen(false);
              }} />`)}
          <//>
        <//>`
      : null}
  </div>`;
}

/**
 * Busca em qualquer campo (AnyFieldSearchChip do Twenty): chip com o texto, editável no próprio chip. A lista só
 * busca depois de uma pausa na digitação, para não consultar o banco a cada tecla.
 * @param {{ view: View }} props
 */
function AnyFieldSearchChip({ view }) {
  const draft = getViewDraft(view);
  const [text, setText] = useState(draft.anyFieldSearch.value);
  const inputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const timerRef = useRef(/** @type {ReturnType<typeof setTimeout> | null} */ (null));
  const saved = draft.anyFieldSearch.value;
  // Redefinir, salvar ou abrir um link troca a busca por fora do chip
  useEffect(() => {
    if (timerRef.current === null && saved !== text) setText(saved);
  }, [saved]);
  useEffect(() => {
    if (draft.isAnyFieldSearchOpen.value) requestAnimationFrame(() => inputRef.current?.focus());
  }, [draft.isAnyFieldSearchOpen.value]);
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);
  const commit = (/** @type {string} */ value) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setDraftAnyFieldSearch(view, value);
  };
  const close = () => {
    commit('');
    setText('');
    draft.isAnyFieldSearchOpen.value = false;
  };
  return html`<div class="sort-or-filter-chip any-field-chip" data-testid="any-field-chip">
    <label class="sort-or-filter-chip-main">
      <${Icon} name="IconSearch" size=${16} />
      <span class="sort-or-filter-chip-label">${t('viewsFilters.anyField.chipLabel')}</span>
      <input ref=${inputRef} class="any-field-chip-input" type="search" value=${text} maxlength="100"
        placeholder=${t('viewsFilters.anyField.placeholder')} aria-label=${t('viewsFilters.anyField.menuItem')}
        onInput=${(event) => {
          const value = event.currentTarget.value;
          setText(value);
          if (timerRef.current) clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => commit(value), 350);
        }}
        onKeyDown=${(event) => {
          if (event.key === 'Enter') commit(event.currentTarget.value);
          if (event.key === 'Escape') {
            event.stopPropagation();
            if (event.currentTarget.value === '') close();
            else event.currentTarget.blur();
          }
        }}
        onBlur=${(event) => {
          commit(event.currentTarget.value);
          if (event.currentTarget.value.trim() === '') draft.isAnyFieldSearchOpen.value = false;
        }} />
    </label>
    <button type="button" class="sort-or-filter-chip-remove" aria-label=${t('viewsFilters.anyField.remove')} onClick=${close}>
      <${Icon} name="IconX" size=${14} />
    </button>
  </div>`;
}

/**
 * @param {{ objectMetadata: ObjectMetadata, view: View, totalCount: number | null, hiddenCount?: number | null }} props
 *   hiddenCount: registros em colunas ocultas do kanban (aviso do chip do painel)
 */
export function ViewBar({ objectMetadata, view, totalCount, hiddenCount = null }) {
  const draft = getViewDraft(view);
  const groupIds = new Set(draft.filterGroups.value.map((group) => group.id));
  // regras do filtro avançado aparecem no chip próprio, não como chips soltos
  const filters = draft.filters.value.filter((filter) => !filter.viewFilterGroupId || !groupIds.has(filter.viewFilterGroupId));
  const hasAdvancedFilter = draft.filterGroups.value.length > 0;
  const sorts = draft.sorts.value;
  const isDirty = isViewDraftDirty(view);
  const isDeletedActive = isDeletedFilterActive(view);
  const showAnyFieldSearch = draft.anyFieldSearch.value.trim() !== '' || draft.isAnyFieldSearchOpen.value;
  const drilldown = getViewDrilldown(view);
  const drilldownMode = drilldownModeOf(drilldown);
  const showDetails = isDirty || filters.length > 0 || sorts.length > 0 || hasAdvancedFilter || isDeletedActive || showAnyFieldSearch || Boolean(drilldown);
  // A leitura de locationState mantém a barra sincronizada com ?viewId.
  void locationState.value;
  const queryKey = getViewQuery(view).key;
  // a URL acompanha o rascunho: o endereço da barra já é o link da lista filtrada (doc 06, seção 14)
  useEffect(() => {
    syncViewUrlWithDraft(view);
  }, [view.id, queryKey, isDirty]);

  return html`<div class="view-bar">
    <div class="view-bar-top">
      <${ViewPicker} objectMetadata=${objectMetadata} view=${view} totalCount=${totalCount} isDirty=${isDirty} />
      <div class="view-bar-actions">
        <button type="button" class="header-dropdown-button" data-testid="any-field-search-button" aria-label=${t('viewsFilters.anyField.menuItem')}
          title=${t('viewsFilters.anyField.menuItem')} onClick=${() => (draft.isAnyFieldSearchOpen.value = true)}>
          <${Icon} name="IconSearch" size=${16} />
        </button>
        <${AddFilterButton} objectMetadata=${objectMetadata} view=${view} label=${t('viewBar.filter')} answersCommands=${true} />
        <${SortButton} objectMetadata=${objectMetadata} view=${view} />
        <${ViewOptionsButton} objectMetadata=${objectMetadata} view=${view} />
        ${hasCurrentPermissionFlag('IMPORT_CSV') && canUpdateObjectRecords(objectMetadata)
          ? html`<${ImportRecordsButton} objectMetadata=${objectMetadata} viewId=${view.id} />`
          : null}
        ${hasCurrentPermissionFlag('EXPORT_CSV') ? html`<${ExportRecordsButton} objectMetadata=${objectMetadata} view=${view} />` : null}
      </div>
    </div>
    ${showDetails
      ? html`<div class="view-bar-details" data-drilldown=${drilldown ? 'true' : undefined}>
          ${drilldown
            ? html`<${DrilldownChip} key=${drilldown} parameter=${drilldown} totalCount=${totalCount} viewType=${view.type} hiddenCount=${hiddenCount}
                onClear=${() => clearViewDrilldown(view)} />`
            : null}
          <div class="view-bar-chips">
            ${isDeletedActive ? html`<${DeletedFilterChip} view=${view} />` : null}
            ${isDeletedActive && (sorts.length > 0 || filters.length > 0 || hasAdvancedFilter) ? html`<span class="view-bar-separator"></span>` : null}
            ${sorts.map((sort) => html`<${SortChip} key=${sort.id} view=${view} sort=${sort} />`)}
            ${sorts.length > 0 && (filters.length > 0 || hasAdvancedFilter || showAnyFieldSearch) ? html`<span class="view-bar-separator"></span>` : null}
            ${showAnyFieldSearch ? html`<${AnyFieldSearchChip} key=${`search-${view.id}`} view=${view} />` : null}
            <${AdvancedFilterChip} objectMetadata=${objectMetadata} view=${view} />
            ${filters.map((filter) => html`<${FilterChip} key=${filter.id} view=${view} filter=${filter} />`)}
            <${AddFilterButton}
              objectMetadata=${objectMetadata}
              view=${view}
              icon="IconPlus"
              label=${filters.length === 0 ? t('viewBar.addFilter') : null}
              buttonClassName="view-bar-add-filter"
            />
          </div>
          ${drilldown
            ? html`<div class="view-bar-update">
                ${drilldownMode === 'FILTER'
                  ? html`<${SaveAsNewViewButton} objectMetadata=${objectMetadata} view=${view} onSaved=${() => resetViewDraft(view)} />`
                  : html`<span class="view-bar-update-hint">${t(isFlowDrilldownRecipe(recipeOf(drilldown)) ? 'dashboardDrilldown.chip.saveDisabledFlow' : 'dashboardDrilldown.chip.saveDisabled')}</span>`}
              </div>`
            : isDirty
            ? html`<div class="view-bar-update">
                <${LightButton} title=${t('viewBar.reset')} accent="tertiary" onClick=${() => resetViewDraft(view)} />
                <${SaveAsNewViewButton} objectMetadata=${objectMetadata} view=${view} onSaved=${() => resetViewDraft(view)} />
                <${Button} title=${t('viewBar.save')} size="small" variant="secondary" accent="blue" onClick=${() => saveViewDraft(view)} />
              </div>`
            : null}
        </div>`
      : null}
  </div>`;
}

